import { useRef, useEffect } from 'react';
import { CANVAS_CONFIG } from '@beat-games/game-config';
import type { ReplayState } from './ReplayEngine';

interface ReplayCanvasProps {
  state: ReplayState | null;
  walletAddress?: string;
  phaseType?: string;
}

// Danger zone thresholds (matching GameCanvas)
const DANGER_ZONE_START = 0.8;
const DANGER_ZONE_FULL = 0.95;

// Colors for danger states
const COLOR_SAFE = { r: 255, g: 255, b: 255 };
const COLOR_WARNING = { r: 251, g: 146, b: 60 };
const COLOR_DANGER = { r: 239, g: 68, b: 68 };

function lerpColor(
  color1: { r: number; g: number; b: number },
  color2: { r: number; g: number; b: number },
  t: number
): { r: number; g: number; b: number } {
  return {
    r: Math.round(color1.r + (color2.r - color1.r) * t),
    g: Math.round(color1.g + (color2.g - color1.g) * t),
    b: Math.round(color1.b + (color2.b - color1.b) * t),
  };
}

function calculateDangerLevel(
  cursorX: number,
  cursorY: number,
  circleX: number,
  circleY: number,
  radius: number
): number {
  const dx = cursorX - circleX;
  const dy = cursorY - circleY;
  const distance = Math.sqrt(dx * dx + dy * dy);
  return distance / radius;
}

function getDangerColor(dangerLevel: number): { r: number; g: number; b: number } {
  if (dangerLevel > 1) {
    return COLOR_DANGER;
  } else if (dangerLevel > DANGER_ZONE_FULL) {
    const t = (dangerLevel - DANGER_ZONE_FULL) / (1 - DANGER_ZONE_FULL);
    return lerpColor(COLOR_WARNING, COLOR_DANGER, t);
  } else if (dangerLevel > DANGER_ZONE_START) {
    const t = (dangerLevel - DANGER_ZONE_START) / (DANGER_ZONE_FULL - DANGER_ZONE_START);
    return lerpColor(COLOR_SAFE, COLOR_WARNING, t);
  }
  return COLOR_SAFE;
}

function formatTime(ms: number): string {
  const seconds = ms / 1000;
  return seconds.toFixed(2) + 's';
}

function formatWallet(address: string): string {
  if (address.length <= 11) return address;
  return `${address.slice(0, 4)}...${address.slice(-4)}`;
}

export default function ReplayCanvas({ state, walletAddress, phaseType }: ReplayCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const render = () => {
      // Clear canvas
      ctx.fillStyle = CANVAS_CONFIG.backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw subtle grid pattern
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.02)';
      ctx.lineWidth = 1;
      const gridSize = 40;
      for (let x = gridSize; x < canvas.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      for (let y = gridSize; y < canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }

      // Draw scanline effect for broadcast feel
      ctx.fillStyle = 'rgba(255, 255, 255, 0.008)';
      for (let y = 0; y < canvas.height; y += 4) {
        ctx.fillRect(0, y, canvas.width, 1);
      }

      if (state) {
        const { circle, cursor } = state;

        // Calculate danger level
        const dangerLevel = calculateDangerLevel(
          cursor.x,
          cursor.y,
          circle.x,
          circle.y,
          circle.radius
        );

        const dangerColor = getDangerColor(dangerLevel);
        const isInDanger = dangerLevel > DANGER_ZONE_START;
        const isOutside = dangerLevel > 1;

        // Pulsing effect for danger zone
        const pulseIntensity = isInDanger && !isOutside
          ? 0.5 + Math.sin(Date.now() * 0.016 * Math.PI) * 0.5
          : 1;

        // Outer glow effect
        const glowGradient = ctx.createRadialGradient(
          circle.x,
          circle.y,
          circle.radius,
          circle.x,
          circle.y,
          circle.radius * 1.8
        );
        const glowOpacity = isInDanger ? 0.12 * pulseIntensity : 0.08;
        glowGradient.addColorStop(0, `rgba(${dangerColor.r}, ${dangerColor.g}, ${dangerColor.b}, ${glowOpacity})`);
        glowGradient.addColorStop(0.5, `rgba(${dangerColor.r}, ${dangerColor.g}, ${dangerColor.b}, ${glowOpacity * 0.4})`);
        glowGradient.addColorStop(1, `rgba(${dangerColor.r}, ${dangerColor.g}, ${dangerColor.b}, 0)`);
        ctx.fillStyle = glowGradient;
        ctx.beginPath();
        ctx.arc(circle.x, circle.y, circle.radius * 1.8, 0, Math.PI * 2);
        ctx.fill();

        // Pulsing glow ring when in danger zone
        if (isInDanger && !isOutside) {
          const pulseRadius = circle.radius * (1.1 + pulseIntensity * 0.15);
          ctx.strokeStyle = `rgba(${dangerColor.r}, ${dangerColor.g}, ${dangerColor.b}, ${0.3 * pulseIntensity})`;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(circle.x, circle.y, pulseRadius, 0, Math.PI * 2);
          ctx.stroke();
        }

        // Main circle border
        ctx.strokeStyle = `rgb(${dangerColor.r}, ${dangerColor.g}, ${dangerColor.b})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(circle.x, circle.y, circle.radius, 0, Math.PI * 2);
        ctx.stroke();

        // Inner fill
        const fillGradient = ctx.createRadialGradient(
          circle.x,
          circle.y,
          0,
          circle.x,
          circle.y,
          circle.radius
        );
        const fillOpacityCenter = isOutside ? 0.15 : (isInDanger ? 0.1 : 0.08);
        const fillOpacityEdge = isOutside ? 0.05 : (isInDanger ? 0.04 : 0.02);
        fillGradient.addColorStop(0, `rgba(${dangerColor.r}, ${dangerColor.g}, ${dangerColor.b}, ${fillOpacityCenter})`);
        fillGradient.addColorStop(1, `rgba(${dangerColor.r}, ${dangerColor.g}, ${dangerColor.b}, ${fillOpacityEdge})`);
        ctx.fillStyle = fillGradient;
        ctx.beginPath();
        ctx.arc(circle.x, circle.y, circle.radius, 0, Math.PI * 2);
        ctx.fill();

        // Cursor glow
        const cursorColor = getDangerColor(dangerLevel);
        const cursorGlow = ctx.createRadialGradient(
          cursor.x,
          cursor.y,
          0,
          cursor.x,
          cursor.y,
          12
        );
        cursorGlow.addColorStop(0, `rgba(${cursorColor.r}, ${cursorColor.g}, ${cursorColor.b}, 0.3)`);
        cursorGlow.addColorStop(1, 'transparent');
        ctx.fillStyle = cursorGlow;
        ctx.beginPath();
        ctx.arc(cursor.x, cursor.y, 12, 0, Math.PI * 2);
        ctx.fill();

        // Cursor dot
        ctx.fillStyle = `rgb(${cursorColor.r}, ${cursorColor.g}, ${cursorColor.b})`;
        ctx.beginPath();
        ctx.arc(cursor.x, cursor.y, 4, 0, Math.PI * 2);
        ctx.fill();

        // HUD overlay - Top left: Player info
        ctx.save();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
        ctx.fillRect(12, 12, 180, 50);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.lineWidth = 1;
        ctx.strokeRect(12, 12, 180, 50);

        if (walletAddress) {
          ctx.fillStyle = '#525252';
          ctx.font = '500 10px "JetBrains Mono", monospace';
          ctx.textAlign = 'left';
          ctx.fillText('PLAYER', 20, 28);

          ctx.fillStyle = '#ffffff';
          ctx.font = '600 14px "JetBrains Mono", monospace';
          ctx.fillText(formatWallet(walletAddress), 20, 46);
        }
        ctx.restore();

        // HUD overlay - Top right: Phase badge
        if (phaseType) {
          ctx.save();
          const phaseColors: Record<string, string> = {
            QUALIFIER: '#22c55e',
            SHORTLIST: '#3b82f6',
            SEMIFINAL: '#a855f7',
            FINAL: '#fbbf24',
          };
          const phaseColor = phaseColors[phaseType] || '#ffffff';

          ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
          ctx.fillRect(canvas.width - 112, 12, 100, 28);
          ctx.strokeStyle = phaseColor + '40';
          ctx.lineWidth = 1;
          ctx.strokeRect(canvas.width - 112, 12, 100, 28);

          ctx.fillStyle = phaseColor;
          ctx.font = '700 11px "JetBrains Mono", monospace';
          ctx.textAlign = 'center';
          ctx.fillText(phaseType, canvas.width - 62, 30);
          ctx.restore();
        }

        // HUD overlay - Bottom center: Time
        ctx.save();
        const timeBoxWidth = 140;
        const timeBoxX = (canvas.width - timeBoxWidth) / 2;
        ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
        ctx.fillRect(timeBoxX, canvas.height - 52, timeBoxWidth, 40);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.lineWidth = 1;
        ctx.strokeRect(timeBoxX, canvas.height - 52, timeBoxWidth, 40);

        // Time display
        ctx.fillStyle = '#a1a1a1';
        ctx.font = '500 10px "JetBrains Mono", monospace';
        ctx.textAlign = 'center';
        ctx.fillText('ELAPSED', canvas.width / 2, canvas.height - 36);

        ctx.fillStyle = '#ffffff';
        ctx.font = '700 18px "JetBrains Mono", monospace';
        ctx.fillText(formatTime(state.elapsed), canvas.width / 2, canvas.height - 18);
        ctx.restore();

        // Corner decorations for broadcast feel
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
        ctx.lineWidth = 2;

        // Top-left corner
        ctx.beginPath();
        ctx.moveTo(4, 24);
        ctx.lineTo(4, 4);
        ctx.lineTo(24, 4);
        ctx.stroke();

        // Top-right corner
        ctx.beginPath();
        ctx.moveTo(canvas.width - 24, 4);
        ctx.lineTo(canvas.width - 4, 4);
        ctx.lineTo(canvas.width - 4, 24);
        ctx.stroke();

        // Bottom-left corner
        ctx.beginPath();
        ctx.moveTo(4, canvas.height - 24);
        ctx.lineTo(4, canvas.height - 4);
        ctx.lineTo(24, canvas.height - 4);
        ctx.stroke();

        // Bottom-right corner
        ctx.beginPath();
        ctx.moveTo(canvas.width - 24, canvas.height - 4);
        ctx.lineTo(canvas.width - 4, canvas.height - 4);
        ctx.lineTo(canvas.width - 4, canvas.height - 24);
        ctx.stroke();

      } else {
        // No replay loaded state
        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;

        // Breathing animation
        const breathe = Math.sin(Date.now() / 800) * 3;
        const baseRadius = 50 + breathe;

        // Outer ring
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(centerX, centerY, baseRadius + 15, 0, Math.PI * 2);
        ctx.stroke();

        // Inner ring
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
        ctx.beginPath();
        ctx.arc(centerX, centerY, baseRadius, 0, Math.PI * 2);
        ctx.stroke();

        // Center glow
        const glow = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, baseRadius);
        glow.addColorStop(0, 'rgba(255, 255, 255, 0.05)');
        glow.addColorStop(1, 'transparent');
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(centerX, centerY, baseRadius, 0, Math.PI * 2);
        ctx.fill();

        // Text
        ctx.fillStyle = '#525252';
        ctx.font = '500 12px "JetBrains Mono", monospace';
        ctx.textAlign = 'center';
        ctx.fillText('SELECT A REPLAY', centerX, centerY + 4);
      }

      animationRef.current = requestAnimationFrame(render);
    };

    render();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [state, walletAddress, phaseType]);

  return (
    <canvas
      ref={canvasRef}
      width={CANVAS_CONFIG.width}
      height={CANVAS_CONFIG.height}
      className="w-full h-full rounded-lg"
      style={{ aspectRatio: `${CANVAS_CONFIG.width} / ${CANVAS_CONFIG.height}` }}
    />
  );
}
